import { Component } from '@angular/core'
import { Emittable, Emitter } from '@ngxs-labs/emitter'
import { PlugplayState } from '../../states/plugplay-state.service'
import { CoreService } from '../../../../services'
import { DialogService } from 'primeng/dynamicdialog'
import * as XLSX from 'xlsx'
import { Product } from 'depoto-core/src/entities/index'
import { InvalidListComponent } from '../../dialogs/invalid-list/invalid-list.component'
import { Export } from 'depoto-core/src/entities'
import { OptionsConfig } from '../../../../configs'
import { TranslateService } from '@ngx-translate/core'
import { FileUtilService } from '../../../../utils/file-util.service'

@Component({
  selector: 'app-step7-import',
  templateUrl: './step7-import.component.html',
  styleUrls: ['./step7-import.component.scss'],
})
export class Step7ImportComponent {
  @Emitter(PlugplayState.setActive)
  private readonly setActive: Emittable<number>

  private supplierList: Array<{ id: null; name: null }>
  public showSkipMessage: boolean
  public showProgress: boolean
  private xlsxData: any
  public progressValue = 0
  file: File
  arrayBuffer: any
  itemCount: number
  fileList: any
  showStart = true
  errorList = new Array<{ line: number; error: string }>()
  exportLoading = true
  exportUrl = ''
  private vatList: Array<number> = []

  constructor(
    public core: CoreService,
    public dialogService: DialogService,
    private readonly translateService: TranslateService,
    private readonly fileUtil: FileUtilService,
  ) {
    this.setActive.emit(6)
    this.core.services.supplier
      .getList({ page: 0, sort: 'id', direction: 'DESC' }, { id: null, name: null })
      .then(response => (this.supplierList = response.items))
    this.core.services.export
      .create(new Export({ type: 'hs-wizard-products' }), { id: null, url: null, file: { mimeType: null } })
      .then(value => this.export(value.id))
    this.core.services.vat
      .getList({}, { percent: null })
      .then(value => (this.vatList = value.items.map(vat => vat.percent)))
  }

  private export(id: number) {
    this.core.services.export.getById(id, { id: null, url: null }).then(response => {
      if (response.url) {
        this.exportLoading = false
        this.exportUrl = response.url
      } else {
        setTimeout(this.export.bind(this, id), 1000)
      }
    })
  }

  async downloadFile() {
    await this.fileUtil.downloadFile(this.exportUrl, 'product-check.xlsx')
  }

  selected(event) {
    this.errorList = []
    this.file = event.addedFiles
      .filter(f => f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      .pop()
    if (this.file) {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(this.file)
      fileReader.onload = e => {
        this.arrayBuffer = fileReader.result
        const data = new Uint8Array(this.arrayBuffer)
        const arr = []
        for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i])
        const bstr = arr.join('')
        const workbook = XLSX.read(bstr, { type: 'binary' })
        const first_sheet_name = workbook.SheetNames[0]
        const header: Array<string> = XLSX.utils
          .sheet_to_json(workbook.Sheets[first_sheet_name], { header: 1 })
          .shift() as Array<string>
        this.xlsxData = XLSX.utils.sheet_to_json(workbook.Sheets[first_sheet_name], { raw: true }).map(row => {
          const temp = {}
          header.forEach((key, index) => (temp[index] = row[key]))
          return temp
        })
        this.itemCount = this.xlsxData.length

        this.validate(this.xlsxData)
        this.showStart = false
        this.showSkipMessage = true
      }
    }
  }

  private validate(list: Array<any>) {
    list.forEach((value, index) => {
      const error = this.errorType(value)
      if (error !== ERROR_TYPE.IS_EVERYTHING_OK) {
        this.errorList.push({ line: index, error })
      }
    })
  }

  private errorType(line: any): ERROR_TYPE {
    if (!line[COLUMNS.NAME]) {
      return ERROR_TYPE.NAME
    }
    const supplierDoesNotExist = this.supplierList.findIndex(value => value.name === line[COLUMNS.SUPPLIER]) === -1
    if (supplierDoesNotExist && !!line[COLUMNS.SUPPLIER]) {
      return ERROR_TYPE.SUPPLIER
    }
    if (isNaN(line[COLUMNS.PRICE]) && !!line[COLUMNS.PRICE]) {
      return ERROR_TYPE.PRICE
    }
    if (!this.vatList.includes(line[COLUMNS.VAT]) || line[COLUMNS.VAT] === undefined) {
      return ERROR_TYPE.VAT
    }

    return ERROR_TYPE.IS_EVERYTHING_OK
  }

  public import(): void {
    this.showProgress = true
    this.showSkipMessage = false
    const result = this.transformData(this.xlsxData)
    this.save(result)
  }

  private transformData(data: Array<any>): Array<any> {
    const result = data.map((row, index) => {
      if (!this.errorList.find(value => value.line === index)) {
        const prod = new Product({
          id: row[COLUMNS.PRODUCT_ID],
          name: row[COLUMNS.NAME],
          purchaseCurrency: this.core.services.user.user?.company?.currency,
          vat:
            row[COLUMNS.VAT] !== undefined
              ? { id: this.core.lists.vats.filter(value => value.percent === row[COLUMNS.VAT])[0].id }
              : null,
          purchasePrice: row[COLUMNS.PRICE],
          ean: row[COLUMNS.EAN],
          code3: row[COLUMNS.PRODUCER],
          supplier: row[COLUMNS.SUPPLIER]
            ? { id: this.supplierList.filter(value => value.name === row[COLUMNS.SUPPLIER])[0].id }
            : null,
        })
        if ([null, undefined, ''].includes(row[COLUMNS.PRODUCT_ID])) {
          prod.id = undefined
        }

        return (({ ean, id, name, purchaseCurrency, purchasePrice, supplier, vat, code3 }) => ({
          ean,
          id,
          name,
          purchaseCurrency,
          purchasePrice,
          supplier,
          vat,
          code3,
        }))(prod)
      }
    })
    return result
  }

  showInvalidData() {
    this.dialogService.open(InvalidListComponent, {
      header: this.translateService.instant('wizard.invalid-dialog.title'),
      data: {
        errorList: this.errorList,
      },
      style: {
        width: '600px',
        height: 'auto',
      },
    })
  }

  private async save(products: Array<Product>) {
    this.progressValue = 0
    for (const prod of products) {
      if (prod) {
        if (prod.id) {
          await this.core.services.product.updateProductPart(prod)
          this.progressValue += 1
        } else {
          await this.core.services.product.create(prod)
          this.progressValue += 1
        }
      }
    }
  }
}

enum ERROR_TYPE {
  NAME = 'wizard.step-7.error.name',
  SUPPLIER = 'wizard.step-7.error.supplier',
  PRICE = 'wizard.step-7.error.price',
  VAT = 'wizard.step-7.error.vat',
  IS_EVERYTHING_OK = 'true',
  PRODUCT = 'wizard.step-7.error.product',
}

enum COLUMNS {
  NAME = 1,
  SUPPLIER = 4,
  PRICE = 5,
  VAT = 6,
  EAN = 3,
  PRODUCER = 2,
  PRODUCT_ID = 0,
}
